import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Events() {
  const [events, setEvents] = useState(null);
  const [view, setView] = useState(() => {
    const t = new Date();
    return { y: t.getFullYear(), m: t.getMonth() };
  });
  const [selected, setSelected] = useState(null);

  const now = new Date();
  const todayKey = toKey(now.getFullYear(), now.getMonth(), now.getDate());

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((r) => {
        const all = r.data.all || [];
        setEvents(all);
        const next = all.find((e) => (e.date || "") >= todayKey);
        if (next) {
          const [y, m] = next.date.split("-").map(Number);
          setView({ y, m: m - 1 });
          setSelected(next.date);
        }
      })
      .catch(() => setEvents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (!e.date) return;
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const offset = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const shift = (delta) => {
    const d = new Date(view.y, view.m + delta, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  };

  const selectedEvents = selected ? byDate[selected] || [] : [];

  return (
    <main data-testid="events-page">
      <Seo
        title="Events Calendar — Whitley Bay Cricket Club"
        description="The club calendar at Whitley Bay CC: presentation evenings, indoor nets, All Stars and social nights at West Park."
      />
      <PageHeader
        eyebrow="Club calendar"
        title="What's on at"
        accent="West Park"
        description="Presentation nights, indoor nets, All Stars and everything in between. Dates marked in gold have club events — select one for the details."
        image={IMAGES.clubhouseLounge}
        testId="events-header"
      />

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24" data-testid="events-calendar">
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-[#122030] p-5 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <button
                type="button"
                onClick={() => shift(-1)}
                data-testid="events-cal-prev"
                aria-label="Previous month"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#9BB0C1] hover:border-[#E5AC3C] hover:text-[#E5AC3C] transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <h2 className="font-head text-xl sm:text-2xl font-extrabold tracking-tight" data-testid="events-cal-month">
                {monthLabel}
              </h2>
              <button
                type="button"
                onClick={() => shift(1)}
                data-testid="events-cal-next"
                aria-label="Next month"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#9BB0C1] hover:border-[#E5AC3C] hover:text-[#E5AC3C] transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285] py-1">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="events-cal-grid">
              {cells.map((day, i) => {
                if (day === null) return <div key={`empty-${i}`} aria-hidden="true" />;
                const key = toKey(view.y, view.m, day);
                const dayEvents = byDate[key] || [];
                const hasEvents = dayEvents.length > 0;
                const isSelected = selected === key;
                const isToday = key === todayKey;
                const isPast = key < todayKey;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`cal-day-${key}`}
                    onClick={() => hasEvents && setSelected(key)}
                    disabled={!hasEvents}
                    className={`relative aspect-square rounded-xl border text-sm font-semibold flex flex-col items-center justify-center gap-0.5 transition-colors ${
                      isSelected
                        ? "border-[#E5AC3C] bg-[#E5AC3C] text-[#0B1520]"
                        : hasEvents
                        ? "border-[#E5AC3C]/50 bg-[#E5AC3C]/10 text-[#E5AC3C] hover:bg-[#E5AC3C]/20 cursor-pointer"
                        : isToday
                        ? "border-white/30 text-[#F7F5F0]"
                        : "border-white/5 text-[#9BB0C1]"
                    } ${isPast && !isSelected ? "opacity-45" : ""}`}
                  >
                    {day}
                    {hasEvents && (
                      <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-[#0B1520]" : "bg-[#E5AC3C]"}`} aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>

            <p className="mt-4 text-xs text-[#5C7285] inline-flex items-center gap-1.5">
              <CalendarDays size={12} /> Dates marked in gold have club events — select one for the details.
            </p>
          </div>
        </Reveal>

        {selectedEvents.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="events-cal-detail">
            {selectedEvents.map((ev) => (
              <Reveal key={ev.id}>
                <div className="rounded-2xl border border-[#E5AC3C]/40 bg-[#122030] p-5 sm:p-6 flex items-center gap-5" data-testid={`cal-event-${ev.id}`}>
                  <div className="shrink-0 w-16 text-center rounded-xl bg-[#0B1520] border border-white/10 py-2.5">
                    <div className="font-head text-xl font-black text-[#E5AC3C]">{(ev.date || "").slice(8, 10)}</div>
                    <div className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">
                      {new Date(`${ev.date}T12:00:00`).toLocaleDateString("en-GB", { month: "short" })}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{ev.category}</span>
                    <h3 className="font-head font-bold leading-snug">{ev.title}</h3>
                    {ev.description && <p className="mt-1.5 text-sm text-[#9BB0C1] leading-relaxed">{ev.description}</p>}
                    <p className="mt-2 text-xs text-[#5C7285] flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {ev.time || "Time TBA"}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> West Park, Whitley Bay</span>
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {events !== null && events.length === 0 && (
          <p className="mt-6 text-sm text-[#9BB0C1]" data-testid="events-empty">
            No events at the moment — check back soon.
          </p>
        )}
      </section>
    </main>
  );
}
